import { Component, Input } from '@angular/core';
import { DialogModule } from "primeng/dialog";
import { ToastComponent } from '../../../../shared/components/toast/toast.component';
import { CommonModule } from '@angular/common';
import { TranslocoPipe } from '@jsverse/transloco';
import { CustomDropdownComponent } from '../../../../shared/components/custom-dropdown/custom-dropdown.component';
import { CustomNumberInputComponent } from '../../../../shared/components/custom-number-input/custom-number-input.component';
import { LabeledInputComponent } from '../../../../shared/components/labeled-input/labeled-input.component';
import { CustomDateInputComponent } from '../../../../shared/components/custom-date-input/custom-date-input.component';
import { CustomButtonComponent } from "../../../../shared/components/custom-button/custom-button.component";
import { CommodityType, ConsumptionClass, ContractType, FileType, InvoiceElectricityData, InvoiceElectricityDataItem, InvoiceElectricityExtractedData, PriceType } from '../../../../core/interfaces/invoice-interfaces';
import { DateConversionService } from '../../../../core/services/date-conversion.service';
import { JSONGeneratorHistoryElectricityCreate, JSONGeneratorHistoryElectricityGet } from '../../../../core/interfaces/admin-interfaces';
import { AdminService } from '../../../../core/api/admin.service';
import { lastValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiErrorService } from '../../../../core/api/api-error.service';

@Component({
  selector: 'app-generate-electricity-jsondialog',
  standalone: true,
  imports: [DialogModule, CommonModule, TranslocoPipe, CustomDropdownComponent, CustomNumberInputComponent, LabeledInputComponent, CustomDateInputComponent, CustomButtonComponent],
  templateUrl: './generate-electricity-jsondialog.component.html',
  styleUrl: './generate-electricity-jsondialog.component.css'
})
export class GenerateElectricityJSONDialogComponent {
  constructor(private dateService: DateConversionService, private adminService: AdminService, private apiError: ApiErrorService) { }
  @Input() toast!: ToastComponent;
  @Input() reloadCallbackFunction!: () => void;

  visible: boolean = false;

  saveClicked: boolean = false;
  successful: boolean = false;
  dialogErrorMessage: string | null = null;

  // Dropdown
  contractTypeDropdownOptions: string[] = [];
  priceTypeDropdownOptions: string[] = [];
  consumptionClassDropdownOptions: string[] = [];
  selectedConsumptionClassDropdown: number | undefined;

  // Record Data
  recordData: JSONGeneratorHistoryElectricityGet | null = null;

  // Base invoice inputs
  invoiceNumberInput: string = '';
  totalConsumptionInput: string = '';
  invoiceAmountActualInput: string = '';
  invoiceAmountGrossInput: string = '';
  forPeriodDate: Date | undefined;
  totalDiscountInput: string = '';
  vatRateInput: string = '';
  vatAmountInput: string = '';
  invoiceIdInput: string = '';
  invoiceFileNameInput: string = '';
  nontaxableAmountInput: string = '';

  streetInput: string = '';
  numberInput: string = '';
  postalCodeInput: string = '';
  cityInput: string = '';
  provinceInput: string = '';
  latitudeInput: string = '';
  longitudeInput: string = '';

  // Errors
  invoiceNumberInputError: string | null = null;
  totalConsumptionInputError: string | null = null;
  invoiceAmountActualInputError: string | null = null;
  invoiceAmountGrossInputError: string | null = null;
  forPeriodInputError: string | null = null;
  totalDiscountInputError: string | null = null;
  vatRateInputError: string | null = null;
  vatAmountInputError: string | null = null;
  invoiceIdError: string = '';
  invoiceConsumptionClassError: string | null = null;
  nontaxableAmountInputError: string | null = null;

  streetInputError: string | null = null;
  numberInputError: string | null = null;
  postalCodeInputError: string | null = null;
  cityInputError: string | null = null;
  provinceInputError: string | null = null;
  latitudeInputError: string | null = null;
  longitudeInputError: string | null = null;


  // Editable form values extracted from each pod
  editedPods: {
    month_consumption_f1?: string;
    month_consumption_f2?: string;
    month_consumption_f3?: string;
    month_consumption_f0?: string;
    month_consumption_p?: string;
    month_consumption_po?: string;

    pun_price_f1?: string;
    pun_price_f2?: string;
    pun_price_f3?: string;
    pun_price_f0?: string;
    pun_price_p?: string;
    pun_price_po?: string;

    spread_f1?: string;
    spread_f2?: string;
    spread_f3?: string;
    spread_f0?: string;
    spread_p?: string;
    spread_po?: string;

    price_var_f1?: string;
    price_var_f2?: string;
    price_var_f3?: string;
    price_var_f0?: string;
    price_var_p?: string;
    price_var_po?: string;

    price_fix_f1?: string;
    price_fix_f2?: string;
    price_fix_f3?: string;
    price_fix_p?: string;
    price_fix_po?: string;
    price_fix_f0?: string;

    contract_type?: string;
    tariff_code?: string;
    selectedPriceTypeDropdown?: number | undefined;

    network_tension?: string;
    tension_factor?: string;
    power_kw_available?: string;
    power_kw_contracted?: string;
    power_kw_max_consumption?: string;
    total_material_costs?: string;
    dispatch_costs?: string;
    dispatching_factor?: string;
    market_capacity_p?: string;
    market_capacity_op?: string;
    fixed_charge?: string;
    energy_charge_price?: string;
    power_charge_price?: string;
    reactive_energy_costs?: string;
    fixed_costs?: string;
    additional_charges?: string;
    taxrate_1?: string;
    taxrate_2?: string;

    pod_id?: string;
    available_capacity?: string;
    voltage?: string;
    excise_taxes?: string;
    vat_rate?: string;
    vat_amount_actual?: string;

    tms_charges?: string;

    total_taxes?: string;

    selectedContractTypeDropdown?: number | undefined;

    location: {
      street?: string;
      number?: string;
      postal_code?: string;
      city?: string;
      province?: string;
    }

    supplierData: {
      name?: string;
      vat_id?: string;
      contact_name?: string;
      contact_number?: string;
      email?: string;
      start_of_supply?: Date;
      end_of_supply?: Date;
    };
  }[] = [];

  numberOfPods: number = 0;


  openModal(invoice: InvoiceElectricityData | null = null, recordData: JSONGeneratorHistoryElectricityGet | null = null): void {
    this.resetDialogData();

    this.visible = true;
    this.successful = false;
    this.saveClicked = false;

    this.populateStaticDropdowns();

    this.recordData = recordData;

    if (invoice != null) {
      const data = invoice?.data as InvoiceElectricityDataItem;
      this.populateInputsFromInvoice(data, invoice?.id || '', recordData)
      this.numberOfPods = data.pod_data.length;
    }
  }

  closeModal(): void {
    this.visible = false;
  }

  populateStaticDropdowns() {
    this.contractTypeDropdownOptions = [];
    this.contractTypeDropdownOptions = Object.values(ContractType);

    this.priceTypeDropdownOptions = [];
    this.priceTypeDropdownOptions = Object.values(PriceType)

    this.consumptionClassDropdownOptions = [];
    this.consumptionClassDropdownOptions = Object.values(ConsumptionClass)
  }

  getElectricityInvoiceDataFromInputs(): InvoiceElectricityData {
    const invoiceData: InvoiceElectricityData = {
      id: this.invoiceIdInput,
      processed_at: new Date().toISOString() || '',
      type: CommodityType.electricity,
      file_type: FileType.invoice,
      data: {
        location: {
          street: this.streetInput || null,
          number: this.numberInput || null,
          postal_code: this.postalCodeInput || null,
          city: this.cityInput || null,
          province: this.provinceInput || null,
          latitude: parseFloat(this.latitudeInput) ?? null,
          longitude: parseFloat(this.longitudeInput) ?? null
        },
        invoice_data: {
          for_period: this.forPeriodDate && !isNaN(new Date(this.forPeriodDate).getTime())
            ? this.dateService.isoToSimpleDate(new Date(this.forPeriodDate).toISOString())
            : undefined,
          invoice_number: this.invoiceNumberInput.trim(),
          total_consumption: parseFloat(this.totalConsumptionInput),
          invoice_amount_net_actual: parseFloat(this.invoiceAmountActualInput),
          invoice_amount_gross_actual: parseFloat(this.invoiceAmountGrossInput),
          additional_charges: null,
          total_discount_amount: parseFloat(this.totalDiscountInput) ?? null,
          vat_rate: parseFloat(this.vatRateInput) ?? null,
          vat_amount_actual: parseFloat(this.vatAmountInput) ?? null,
          nontaxable_amount: parseFloat(this.nontaxableAmountInput) ?? null,
          consumption_class: this.consumptionClassDropdownOptions[this.selectedConsumptionClassDropdown!] || null
        },
        pod_data: this.editedPods.map((pod, i) => {
          const selectedContractType = this.contractTypeDropdownOptions[pod.selectedContractTypeDropdown!]?.toUpperCase() || null;
          const selectedPriceType = this.priceTypeDropdownOptions[pod.selectedPriceTypeDropdown!]?.toUpperCase() || null;
          return {
            supplier_name: pod.supplierData.name || null,
            total_taxes: parseFloat(pod.total_taxes ?? '') ?? null,
            tms_charges: parseFloat(pod.tms_charges ?? '') ?? null,

            contract_type: selectedContractType ?? null,
            tariff_code: pod?.tariff_code || null,

            month_consumption_f1: parseFloat(pod.month_consumption_f1 ?? '') ?? null,
            month_consumption_f2: parseFloat(pod.month_consumption_f2 ?? '') ?? null,
            month_consumption_f3: parseFloat(pod.month_consumption_f3 ?? '') ?? null,
            month_consumption_f0: parseFloat(pod.month_consumption_f0 ?? '') ?? null,
            month_consumption_p: parseFloat(pod.month_consumption_p ?? '') ?? null,
            month_consumption_po: parseFloat(pod.month_consumption_po ?? '') ?? null,

            price_type: selectedPriceType ?? null,

            pun_price_f1: parseFloat(pod.pun_price_f1 ?? '') ?? null,
            pun_price_f2: parseFloat(pod.pun_price_f2 ?? '') ?? null,
            pun_price_f3: parseFloat(pod.pun_price_f3 ?? '') ?? null,
            pun_price_f0: parseFloat(pod.pun_price_f0 ?? '') ?? null,
            pun_price_p: parseFloat(pod.pun_price_p ?? '') ?? null,
            pun_price_po: parseFloat(pod.pun_price_po ?? '') ?? null,

            spread_f1: parseFloat(pod.spread_f1 ?? '') ?? null,
            spread_f2: parseFloat(pod.spread_f2 ?? '') ?? null,
            spread_f3: parseFloat(pod.spread_f3 ?? '') ?? null,
            spread_f0: parseFloat(pod.spread_f0 ?? '') ?? null,
            spread_po: parseFloat(pod.spread_po ?? '') ?? null,
            spread_p: parseFloat(pod.spread_p ?? '') ?? null,

            price_var_f1: parseFloat(pod.price_var_f1 ?? '') ?? null,
            price_var_f2: parseFloat(pod.price_var_f2 ?? '') ?? null,
            price_var_f3: parseFloat(pod.price_var_f3 ?? '') ?? null,
            price_var_f0: parseFloat(pod.price_var_f0 ?? '') ?? null,
            price_var_p: parseFloat(pod.price_var_p ?? '') ?? null,
            price_var_po: parseFloat(pod.price_var_po ?? '') ?? null,

            price_fix_f1: parseFloat(pod.price_fix_f1 ?? '') ?? null,
            price_fix_f2: parseFloat(pod.price_fix_f2 ?? '') ?? null,
            price_fix_f3: parseFloat(pod.price_fix_f3 ?? '') ?? null,
            price_fix_f0: parseFloat(pod.price_fix_f0 ?? '') ?? null,
            price_fix_p: parseFloat(pod.price_fix_p ?? '') ?? null,
            price_fix_po: parseFloat(pod.price_fix_po ?? '') ?? null,

            network_tension: pod.network_tension ?? null,
            tension_factor: parseFloat(pod.tension_factor ?? '') ?? null,
            power_kw_available: parseFloat(pod.power_kw_available ?? '') ?? null,
            power_kw_contracted: parseFloat(pod.power_kw_contracted ?? '') ?? null,
            power_kw_max_consumption: parseFloat(pod.power_kw_max_consumption ?? '') ?? null,
            total_material_costs: parseFloat(pod.total_material_costs ?? '') ?? null,
            dispatch_costs: parseFloat(pod.dispatch_costs ?? '') ?? null,
            dispatching_factor: parseFloat(pod.dispatching_factor ?? '') ?? null,

            market_capacity_p: parseFloat(pod.market_capacity_p ?? '') ?? null,
            market_capacity_op: parseFloat(pod.market_capacity_op ?? '') ?? null,
            fixed_charge: parseFloat(pod.fixed_charge ?? '') ?? null,
            energy_charge_price: parseFloat(pod.energy_charge_price ?? '') ?? null,
            power_charge_price: parseFloat(pod.power_charge_price ?? '') ?? null,

            reactive_energy_costs: parseFloat(pod.reactive_energy_costs ?? '') ?? null,
            fixed_costs: parseFloat(pod.fixed_costs ?? '') ?? null,
            additional_charges: parseFloat(pod.additional_charges ?? '') ?? null,

            taxrate_1: parseFloat(pod.taxrate_1 ?? '') ?? null,
            taxrate_2: parseFloat(pod.taxrate_2 ?? '') ?? null,

            location: {
              street: pod.location.street || null,
              number: pod.location.number || null,
              postal_code: pod.location.postal_code || null,
              city: pod.location.city || null,
              province: pod.location.province || null
            },

            supplier: {
              name: pod.supplierData.name || null,
              vat_id: pod.supplierData.vat_id || null,
              contact_number: pod.supplierData.contact_number || null,
              contact_name: pod.supplierData.contact_name || null,
              email: pod.supplierData.email || null,
              start_of_supply: pod.supplierData.start_of_supply && !isNaN(new Date(pod.supplierData.start_of_supply).getTime())
                ? this.dateService.isoToSimpleDate(new Date(pod.supplierData.start_of_supply).toISOString())
                : undefined,
              end_of_supply: pod.supplierData.end_of_supply && !isNaN(new Date(pod.supplierData.end_of_supply).getTime())
                ? this.dateService.isoToSimpleDate(new Date(pod.supplierData.end_of_supply).toISOString())
                : undefined
            },

            pod_id: pod?.pod_id || null,
            available_capacity: parseFloat(pod.power_kw_available ?? '') ?? null,
            voltage: parseFloat(pod.voltage ?? '') ?? null,
            excise_taxes: parseFloat(pod.excise_taxes ?? '') ?? null,
            vat_rate: parseFloat(pod.vat_rate ?? '') ?? null,
            vat_amount_actual: parseFloat(pod.vat_amount_actual ?? '') ?? null
          } as InvoiceElectricityExtractedData;
        }),
        historical_data: []
      }
    };

    return invoiceData;
  }

  populateInputsFromInvoice(item: InvoiceElectricityDataItem, id: string, recordData: JSONGeneratorHistoryElectricityGet | null): void {
    this.invoiceIdInput = id ?? '';
    this.invoiceFileNameInput = recordData?.file_name || '';
    this.forPeriodDate = item.invoice_data.for_period ? new Date(item.invoice_data.for_period) : undefined;
    this.invoiceNumberInput = item.invoice_data.invoice_number ?? '';
    this.totalConsumptionInput = item.invoice_data.total_consumption?.toString() ?? '';
    this.invoiceAmountActualInput = item.invoice_data.invoice_amount_net_actual?.toString() ?? '';
    this.invoiceAmountGrossInput = item.invoice_data.invoice_amount_gross_actual?.toString() ?? '';
    this.totalDiscountInput = item.invoice_data.total_discount_amount?.toString() ?? '';
    this.vatRateInput = item.invoice_data.vat_rate?.toString() ?? '';
    this.vatAmountInput = item.invoice_data.vat_amount_actual?.toString() ?? '';
    this.nontaxableAmountInput = item.invoice_data.nontaxable_amount?.toString() ?? '';

    this.streetInput = item.location.street ?? '';
    this.numberInput = item.location.number ?? '';
    this.postalCodeInput = item.location.postal_code ?? '';
    this.cityInput = item.location.city ?? '';
    this.provinceInput = item.location.province ?? '';
    this.latitudeInput = item.location.latitude?.toString() ?? '';
    this.longitudeInput = item.location.longitude?.toString() ?? '';

    this.selectedConsumptionClassDropdown = this.consumptionClassDropdownOptions
      .findIndex(opt => opt.toUpperCase() === (item.invoice_data.consumption_class ?? '').toUpperCase());

    this.editedPods = item.pod_data.map(pod => ({
      // consumption
      month_consumption_f1: pod.month_consumption_f1?.toString() || '',
      month_consumption_f2: pod.month_consumption_f2?.toString() || '',
      month_consumption_f3: pod.month_consumption_f3?.toString() || '',
      month_consumption_f0: pod.month_consumption_f0?.toString() || '',
      month_consumption_p: pod.month_consumption_p?.toString() || '',
      month_consumption_po: pod.month_consumption_po?.toString() || '',

      // prices
      pun_price_f1: pod.pun_price_f1?.toString() || '',
      pun_price_f2: pod.pun_price_f2?.toString() || '',
      pun_price_f3: pod.pun_price_f3?.toString() || '',
      pun_price_f0: pod.pun_price_f0?.toString() || '',
      pun_price_p: pod.pun_price_p?.toString() || '',
      pun_price_po: pod.pun_price_po?.toString() || '',

      spread_f1: pod.spread_f1?.toString() || '',
      spread_f2: pod.spread_f2?.toString() || '',
      spread_f3: pod.spread_f3?.toString() || '',
      spread_f0: pod.spread_f0?.toString() || '',
      spread_p: pod.spread_p?.toString() || '',
      spread_po: pod.spread_po?.toString() || '',

      price_var_f1: pod.price_var_f1?.toString() || '',
      price_var_f2: pod.price_var_f2?.toString() || '',
      price_var_f3: pod.price_var_f3?.toString() || '',
      price_var_f0: pod.price_var_f0?.toString() || '',
      price_var_p: pod.price_var_p?.toString() || '',
      price_var_po: pod.price_var_po?.toString() || '',

      price_fix_f1: pod.price_fix_f1?.toString() || '',
      price_fix_f2: pod.price_fix_f2?.toString() || '',
      price_fix_f3: pod.price_fix_f3?.toString() || '',
      price_fix_f0: pod.price_fix_f0?.toString() || '',
      price_fix_p: pod.price_fix_p?.toString() || '',
      price_fix_po: pod.price_fix_po?.toString() || '',

      // contract and classification
      contract_type: pod.contract_type ?? '',
      tariff_code: pod.tariff_code ?? '',

      // others
      network_tension: pod.network_tension ?? undefined,
      tension_factor: pod.tension_factor?.toString() || '',
      power_kw_available: pod.power_kw_available?.toString() || '',
      power_kw_contracted: pod.power_kw_contracted?.toString() || '',
      power_kw_max_consumption: pod.power_kw_max_consumption?.toString() || '',
      total_material_costs: pod.total_material_costs?.toString() || '',
      dispatch_costs: pod.dispatch_costs?.toString() || '',
      dispatching_factor: pod.dispatching_factor?.toString() || '',
      market_capacity_p: pod.market_capacity_p?.toString() || '',
      market_capacity_op: pod.market_capacity_op?.toString() || '',
      fixed_charge: pod.fixed_charge?.toString() || '',
      energy_charge_price: pod.energy_charge_price?.toString() || '',
      power_charge_price: pod.power_charge_price?.toString() || '',
      reactive_energy_costs: pod.reactive_energy_costs?.toString() || '',
      fixed_costs: pod.fixed_costs?.toString() || '',
      additional_charges: pod.additional_charges?.toString() || '',
      taxrate_1: pod.taxrate_1?.toString() || '',
      taxrate_2: pod.taxrate_2?.toString() || '',
      tms_charges: pod.tms_charges?.toString() || '',
      total_taxes: pod.total_taxes?.toString() || '',
      excise_taxes: pod.excise_taxes?.toString() || '',
      available_capacity: pod.power_kw_available?.toString() || '',
      voltage: pod.voltage?.toString() || '',
      vat_rate: pod.vat_rate?.toString() || '',
      vat_amount_actual: pod.vat_amount_actual?.toString() || '',

      pod_id: pod.pod_id ?? '',

      // location and supplier
      location: {
        street: pod.location?.street ?? '',
        number: pod.location?.number ?? '',
        postal_code: pod.location?.postal_code ?? '',
        city: pod.location?.city ?? '',
        province: pod.location?.province ?? '',
      },
      supplierData: {
        name: pod.supplier?.name ?? '',
        vat_id: pod.supplier?.vat_id ?? '',
        contact_name: pod.supplier?.contact_name ?? '',
        contact_number: pod.supplier?.contact_number ?? '',
        email: pod.supplier?.email ?? '',
        start_of_supply: pod.supplier?.start_of_supply ? new Date(pod.supplier.start_of_supply) : undefined,
        end_of_supply: pod.supplier?.end_of_supply ? new Date(pod.supplier.end_of_supply) : undefined,
      },

      selectedContractTypeDropdown: this.contractTypeDropdownOptions
        .findIndex(opt => opt.toUpperCase() === (pod.contract_type ?? '').toUpperCase()),
      selectedPriceTypeDropdown: this.priceTypeDropdownOptions
        .findIndex(opt => opt.toUpperCase() === (pod.price_type ?? '').toUpperCase()),
    }));
  }


  changePodCount(change: number): void {
    const newCount = this.numberOfPods + change;

    if (newCount < 0) {
      return;
    }

    this.numberOfPods = newCount;

    if (change === -1) {
      // Remove last pod if it exists
      if (this.editedPods.length > 0) {
        this.editedPods.pop();
      }
    } else if (change === 1) {
      // Add a new empty pod
      this.editedPods.push({
        pod_id: '',
        network_tension: '',
        tariff_code: '',
        location: {
          street: '',
          number: '',
          postal_code: '',
          city: '',
          province: ''
        },
        supplierData: {
          name: '',
          vat_id: '',
          contact_name: '',
          contact_number: '',
          email: '',
          start_of_supply: undefined,
          end_of_supply: undefined
        }
      });
    }
  }

  copyText(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      this.toast.showToast('shared.success', 'admin.invite_copied', 'success', 2000, true);
    }).catch(err => {
    });
  }

  copyJson() {
    const invoice = this.getElectricityInvoiceDataFromInputs();
    const json = JSON.stringify(invoice, null, 2);
    this.copyText(json);
  }

  downloadJson() {
    const invoice = this.getElectricityInvoiceDataFromInputs();
    const json = JSON.stringify(invoice, null, 2);

    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;

    const downloadedFileName = this.recordData?.file_name || `electricity_invoice-${new Date().toISOString()}.json`;
    a.download = downloadedFileName;
    
    a.click();

    URL.revokeObjectURL(url);
  }

  resetDialogData() {
    this.numberOfPods = 0;

    this.invoiceNumberInput =
      this.totalConsumptionInput =
      this.invoiceAmountActualInput =
      this.invoiceAmountGrossInput =
      this.totalDiscountInput =
      this.vatRateInput =
      this.vatAmountInput =
      this.nontaxableAmountInput =
      this.invoiceIdInput =
      this.invoiceFileNameInput =
      this.streetInput =
      this.numberInput =
      this.postalCodeInput =
      this.cityInput =
      this.provinceInput =
      this.latitudeInput =
      this.longitudeInput = '';

    this.forPeriodDate = undefined;

    this.selectedConsumptionClassDropdown = undefined;

    this.editedPods = [];

    this.recordData = null;
  }

  async saveJson() {
    const invoiceData = this.getElectricityInvoiceDataFromInputs();
    const newData: JSONGeneratorHistoryElectricityCreate = {
      file_name: this.invoiceFileNameInput || '',
      json_history: invoiceData
    }


    try {
      if (this.recordData == null) {
        await lastValueFrom(this.adminService.createElectricityJsonRecord(newData));
      } else {
        await lastValueFrom(this.adminService.updateElectricityJsonRecord(this.recordData.id, newData));
      }
    }
    catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      return;
    }

    if (this.reloadCallbackFunction) {
      this.reloadCallbackFunction();
    }
    this.closeModal();
  }
}
